import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';

/** Slugs allowed in /help/:slug (must match backend MANUAL_ALLOWED_ROLES basenames). */
const HELP_SLUGS = new Set([
  'quick-start-end-users',
  'end-user-guide',
  'site-administrator-guide',
  'super-user-guide',
  'zerotier-setup',
  'index',
]);

/**
 * Loads an HTML manual from the authenticated API into the app Layout main area.
 * Uses a blob: URL so TOC fragment links (#section) scroll inside the manual.
 * App chrome (header / Help menu) comes from Layout — not from the HTML template.
 */
export default function HelpManualPage() {
  const { slug } = useParams<{ slug: string }>();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const file = slug && HELP_SLUGS.has(slug) ? `${slug}.html` : null;

  useEffect(() => {
    if (!file) {
      setLoading(false);
      setErrorStatus(404);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErrorStatus(null);

    api
      .get<string>(`/manuals/${file}`, {
        responseType: 'text',
        headers: { Accept: 'text/html' },
      })
      .then((res) => {
        if (cancelled) return;
        const origin = window.location.origin;
        // blob: documents do not resolve path-absolute URLs against the app origin.
        // Rewrite those URLs only — leave #fragment TOC links alone (no <base>).
        const injected = res.data.replace(/\b(href|src)="(\/[^"]*)"/g, (full, attr, path) => {
          if (path.startsWith('//')) return full;
          return `${attr}="${origin}${path}"`;
        });
        const nextUrl = URL.createObjectURL(
          new Blob([injected], { type: 'text/html;charset=utf-8' }),
        );
        setBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return nextUrl;
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status ?? 500;
        setErrorStatus(status);
        setBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [file]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const title = useMemo(() => {
    if (!slug) return 'Help';
    return slug
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }, [slug]);

  if (!file) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center py-16 text-muted-foreground">
        Loading {title}…
      </div>
    );
  }

  if (errorStatus === 401) {
    return <Navigate to="/login" replace />;
  }

  if (errorStatus === 403) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-16 text-center">
        <h1 className="text-lg font-semibold text-foreground">You do not have access to this guide</h1>
        <p className="text-sm text-muted-foreground max-w-md">
          This manual is restricted to a different role. Use Help in the navigation for guides available to
          you.
        </p>
        <Button asChild>
          <Link to="/">Back to dashboard</Link>
        </Button>
      </div>
    );
  }

  if (errorStatus || !blobUrl) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-16 text-center">
        <h1 className="text-lg font-semibold text-foreground">Manual unavailable</h1>
        <Button asChild variant="outline">
          <Link to="/">Back to dashboard</Link>
        </Button>
      </div>
    );
  }

  return (
    <iframe
      title={title}
      className="w-full flex-1 min-h-[calc(100vh-4rem)] border-0 bg-[var(--llx-body-bg,#f5f6f7)]"
      src={blobUrl}
      sandbox="allow-same-origin allow-scripts allow-popups allow-top-navigation-by-user-activation"
    />
  );
}
